// ROI Calculator Export Utilities
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import type { Region, ROICalculation, AggregatedResults } from '../types'
import { formatCurrency, formatNumber } from '../data'

interface ROIExportData {
  regions: Region[]
  aggregatedResults: AggregatedResults | null
  getRegionCalculation: (regionId: number) => ROICalculation | null
  customerName?: string
}

// ==================== Text Summary ====================

/**
 * Generate a plain-text summary of the ROI analysis for clipboard copy
 */
export function generateTextSummary(data: ROIExportData): string {
  const { regions, aggregatedResults, getRegionCalculation, customerName } = data

  const lines: string[] = []
  const divider = '─'.repeat(50)

  lines.push('ROI Analysis Summary')
  if (customerName) {
    lines.push(`Customer: ${customerName}`)
  }
  lines.push(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`)
  lines.push(divider)

  // Region details
  regions.forEach((region, index) => {
    const calc = getRegionCalculation(region.id)
    if (!calc) return

    lines.push('')
    lines.push(`Region ${index + 1}: ${region.name || region.country || 'Unnamed'}`)
    lines.push(`  Industry: ${region.industry || 'N/A'}`)
    lines.push(`  Current Provider: ${region.provider || 'N/A'}`)
    lines.push(`  Monthly Volume: ${formatNumber(region.volume)}`)
    lines.push(`  Value per Customer: ${formatCurrency(region.valuePerCustomer)}`)
    lines.push(`  Current Pass Rate: ${region.passRate.toFixed(1)}%`)
    lines.push(`  Projected Pass Rate: ${calc.newRate.toFixed(1)}%`)
    lines.push(`  Pass Rate Improvement: +${calc.improvement.toFixed(1)}%`)
    lines.push(`  Additional Conversions: +${formatNumber(Math.round(calc.additionalConversions))}/mo`)
    lines.push(`  Monthly Value Added: ${formatCurrency(calc.monthlyValue)}`)
    lines.push(`  Annual Value Added: ${formatCurrency(calc.annualValue)}`)

    if (region.stack.length > 0) {
      lines.push(`  Verification Pipeline:`)
      region.stack.forEach((item, i) => {
        lines.push(`    ${i + 1}. ${item.description} (lift: ${item.incrementalLift}%)`)
      })
    }
  })

  // Aggregated results
  if (aggregatedResults) {
    lines.push('')
    lines.push(divider)
    lines.push('AGGREGATED RESULTS')
    lines.push(divider)
    lines.push(`Total Monthly Volume: ${formatNumber(aggregatedResults.totalVolume)}`)
    lines.push(`Current Conversions: ${formatNumber(Math.round(aggregatedResults.totalCurrentConversions))}/mo`)
    lines.push(`Projected Conversions: ${formatNumber(Math.round(aggregatedResults.totalProjectedConversions))}/mo`)
    lines.push(`Additional Conversions: +${formatNumber(Math.round(aggregatedResults.totalAdditionalConversions))}/mo`)
    lines.push(`Avg Pass Rate Improvement: +${aggregatedResults.avgImprovement.toFixed(1)}%`)
    lines.push(`Monthly Value Added: ${formatCurrency(aggregatedResults.totalMonthlyValue)}`)
    lines.push(`Annual Value Added: ${formatCurrency(aggregatedResults.totalAnnualValue)}`)
  }

  lines.push('')
  lines.push('Powered by FrankieOne DealHub')

  return lines.join('\n')
}

// ==================== PDF Export ====================

/**
 * Export ROI analysis to a professional PDF
 */
export function exportROIToPDF(data: ROIExportData): void {
  const { regions, aggregatedResults, getRegionCalculation, customerName } = data
  const doc = new jsPDF()
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  let yPos = 20

  // Title
  doc.setFontSize(18)
  doc.setFont('helvetica', 'bold')
  doc.text('ROI Analysis', 14, yPos)
  yPos += 8

  // Subtitle
  doc.setFontSize(11)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(100, 100, 100)
  if (customerName) {
    doc.text(`Prepared for ${customerName}`, 14, yPos)
    yPos += 6
  }
  doc.text(`Generated ${today}`, 14, yPos)
  doc.setTextColor(0, 0, 0)
  yPos += 12

  // Aggregated Summary (if results exist)
  if (aggregatedResults) {
    doc.setFontSize(13)
    doc.setFont('helvetica', 'bold')
    doc.text('Summary', 14, yPos)
    yPos += 8

    autoTable(doc, {
      startY: yPos,
      head: [['Metric', 'Value']],
      body: [
        ['Total Monthly Volume', formatNumber(aggregatedResults.totalVolume)],
        ['Avg Pass Rate Improvement', `+${aggregatedResults.avgImprovement.toFixed(1)}%`],
        ['Additional Conversions/mo', `+${formatNumber(Math.round(aggregatedResults.totalAdditionalConversions))}`],
        ['Monthly Value Added', formatCurrency(aggregatedResults.totalMonthlyValue)],
        ['Annual Value Added', formatCurrency(aggregatedResults.totalAnnualValue)],
      ],
      theme: 'striped',
      headStyles: {
        fillColor: [68, 114, 196],
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold',
      },
      bodyStyles: { fontSize: 10 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { halign: 'right' },
      },
      margin: { left: 14, right: 14 },
    })

    // @ts-expect-error - jspdf-autotable adds lastAutoTable to doc
    yPos = doc.lastAutoTable.finalY + 12
  }

  // Region Details
  regions.forEach((region, index) => {
    const calc = getRegionCalculation(region.id)
    if (!calc) return

    // Check if we need a new page
    if (yPos > 230) {
      doc.addPage()
      yPos = 20
    }

    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text(`Region ${index + 1}: ${region.name || region.country || 'Unnamed'}`, 14, yPos)
    yPos += 7

    // Region info table
    const regionRows: string[][] = [
      ['Industry', region.industry || 'N/A'],
      ['Current Provider', region.provider || 'N/A'],
      ['Monthly Volume', formatNumber(region.volume)],
      ['Value per Customer', formatCurrency(region.valuePerCustomer)],
      ['Current Pass Rate', `${region.passRate.toFixed(1)}%`],
      ['Projected Pass Rate', `${calc.newRate.toFixed(1)}%`],
      ['Improvement', `+${calc.improvement.toFixed(1)}%`],
      ['Additional Conversions/mo', `+${formatNumber(Math.round(calc.additionalConversions))}`],
      ['Monthly Value Added', formatCurrency(calc.monthlyValue)],
      ['Annual Value Added', formatCurrency(calc.annualValue)],
    ]

    autoTable(doc, {
      startY: yPos,
      body: regionRows,
      theme: 'plain',
      bodyStyles: { fontSize: 9 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 60, textColor: [100, 100, 100] },
        1: { halign: 'right' },
      },
      margin: { left: 14, right: 14 },
    })

    // @ts-expect-error - jspdf-autotable adds lastAutoTable to doc
    yPos = doc.lastAutoTable.finalY + 4

    // Product stack table (if products exist)
    if (region.stack.length > 0) {
      const stackRows = region.stack.map((item, i) => [
        String(i + 1),
        item.description,
        `${item.passRate.toFixed(1)}%`,
        `${item.incrementalLift}%`,
      ])

      autoTable(doc, {
        startY: yPos,
        head: [['#', 'Product', 'Pass Rate', 'Incremental Lift']],
        body: stackRows,
        theme: 'grid',
        headStyles: {
          fillColor: [51, 122, 183],
          textColor: 255,
          fontSize: 8,
          fontStyle: 'bold',
        },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          1: { cellWidth: 90 },
          2: { cellWidth: 30, halign: 'right' },
          3: { cellWidth: 35, halign: 'right' },
        },
        margin: { left: 14, right: 14 },
      })

      // @ts-expect-error - jspdf-autotable adds lastAutoTable to doc
      yPos = doc.lastAutoTable.finalY + 10
    } else {
      yPos += 6
    }
  })

  // Footer
  const pageCount = doc.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(150, 150, 150)
    doc.text(
      `FrankieOne DealHub - ROI Analysis | Page ${i} of ${pageCount}`,
      14,
      doc.internal.pageSize.height - 10
    )
  }

  // Save the PDF
  const filename = customerName
    ? `ROI_Analysis_${customerName.replace(/\s+/g, '_')}.pdf`
    : 'ROI_Analysis.pdf'
  doc.save(filename)
}
